import { EditOutline18Icon } from "@vygruppen/spor-icon-react";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { FormInputs } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/FormInputs";
import { Preview } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Preview";
import { usePreview } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/hooks/usePreview";
import { InfrastructureForm } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { formStateHasCustomField } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/utils";
import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  DropsBadge,
  DropsStaticAlert,
  Flex,
  IconButton,
  Input,
  Stack,
  Text,
} from "shared/components/Spor";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { formatStretchName } from "shared/regex";
import { useStretchBuilder } from "stores/useStretchBuilder";

type DialogContentProps = {
  stretchTitle: string;
};

export const InfrastructureModalContent: FC<DialogContentProps> = ({
  stretchTitle,
}) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<InfrastructureForm>();
  const formState = useWatch({
    control,
    name: "infrastructureForm",
  });

  const [
    selectedLegs,
    stretchBuilderModalState,
    setStretchBuilderModalState,
    setSelectedLegs,
    setSelectedStops,
  ] = useStretchBuilder((state) => [
    state.selectedLegs,
    state.stretchBuilderModalState,
    state.setStretchBuilderModalState,
    state.setSelectedLegs,
    state.setSelectedStops,
  ]);

  const modalState = stretchBuilderModalState.type;

  useEffect(() => {
    if (selectedLegs.length > 0) {
      setValue("infrastructureForm.affectedLegs", selectedLegs);
      setValue(
        "infrastructureForm.stretchName",
        formatStretchName(stretchTitle),
      );
    } else {
      setSelectedLegs(formState.affectedLegs);

      setSelectedStops(
        formState.affectedLegs.flatMap((leg) => [leg.fromStop, leg.toStop]),
      );
    }
  }, []);

  const { previewData, previewStatus } = usePreview(formState);

  if (modalState !== "infrastructure") return null;

  return (
    <ErrorBoundary>
      <Stack gap="30px" width="100%">
        <Flex gap="6px" alignItems="center">
          <Flex gap="3px" flexWrap="wrap" alignItems="center">
            <Text variant="sm">{t("Strekning:")} </Text>
            {stretchTitle.split(", ").map((it) => (
              <DropsBadge
                key={it}
                borderRadius="9px"
                semantics="success"
                paddingY="3px"
                marginX="3px"
              >
                {it}
              </DropsBadge>
            ))}
          </Flex>
          <IconButton
            size="xs"
            variant="tertiary"
            icon={<EditOutline18Icon />}
            aria-label={t("Endre strekning")}
            onClick={() =>
              setStretchBuilderModalState({
                ...stretchBuilderModalState,
                stretchBuilderMode: true,
              })
            }
          />
        </Flex>
        <Stack gap="18px" width="100%">
          <Flex w="100%" flexDir="column" gap="0" position="relative">
            <Input
              label={t("Endre strekningsnavn*")}
              type="string"
              {...register("infrastructureForm.stretchName")}
            />
            <RenderErrorInPath
              errors={errors}
              errorPath="infrastructureForm.stretchName"
            />
          </Flex>
          <FormInputs />
        </Stack>
        <Preview previewStatus={previewStatus} previewData={previewData} />
        {formStateHasCustomField(formState) && (
          <DropsStaticAlert variant="info" id="infoBoxActionModal">
            {t(
              "Hendelsen inneholder egendefinert tekst. Sjekk at oppsummeringen ser riktig ut før du går videre.",
            )}
          </DropsStaticAlert>
        )}
      </Stack>
    </ErrorBoundary>
  );
};
